<template>
  <div class="p-6 h-full overflow-y-auto">
    <!-- Header Section -->
    <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-8 space-y-4 lg:space-y-0">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Painel de Controle</h1>
        <p class="text-sm text-gray-500 mt-1">Bem-vindo de volta! Aqui está um resumo dos seus dados</p>
      </div>
      <div class="flex items-end gap-4">
        <button
          @click="resetToCurrentMonth"
          class="flex items-center gap-2 px-3 py-2 bg-[#003333] text-white text-sm rounded-lg hover:bg-[#004444] transition-colors duration-200"
        >
          <i class="pi pi-calendar"></i>
          Mês Atual
        </button>
        <div class="flex flex-wrap items-end gap-4">
          <!-- Data Inicial -->
          <div class="min-w-[180px]">
            <label class="block text-xs text-gray-500 mb-1">Data Inicial</label>
            <Calendar
              v-model="dates[0]"
              dateFormat="dd/mm/yy"
              placeholder="DD/MM/AAAA"
              :showIcon="true"
              class="w-full"
              :showButtonBar="true"
            />
          </div>
          <!-- Data Final -->
          <div class="min-w-[180px]">
            <label class="block text-xs text-gray-500 mb-1">Data Final</label>
            <Calendar
              v-model="dates[1]"
              dateFormat="dd/mm/yy"
              placeholder="DD/MM/AAAA"
              :showIcon="true"
              class="w-full"
              :showButtonBar="true"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      <div v-for="(stat, index) in stats" :key="index" class="flex-1">
        <div class="surface-card shadow-md border-round-xl p-3 h-full">
          <div class="flex align-items-center justify-content-between">
            <div class="flex align-items-center gap-3">
              <div :class="[
                'flex align-items-center justify-content-center border-round-xl w-3rem h-3rem',
                stat.label === 'Investimento' ? 'bg-purple-50' : '',
                stat.label === 'Pró-Labore' ? 'bg-orange-50' : '',
                stat.label === 'Lucro Líquido' ? 'bg-cyan-50' : '',
                stat.label === 'Receita Total' ? 'bg-green-50' : '',
                stat.label === 'Despesas' ? 'bg-red-50' : '',
                stat.label === 'Margem de Lucro' ? 'bg-blue-50' : ''
              ]">
                <i :class="[
                  stat.icon,
                  'text-xl',
                  stat.label === 'Investimento' ? 'text-purple-500' : '',
                  stat.label === 'Pró-Labore' ? 'text-orange-500' : '',
                  stat.label === 'Lucro Líquido' ? 'text-cyan-500' : '',
                  stat.label === 'Receita Total' ? 'text-green-500' : '',
                  stat.label === 'Despesas' ? 'text-red-500' : '',
                  stat.label === 'Margem de Lucro' ? 'text-blue-500' : ''
                ]"></i>
              </div>
              <div class="flex flex-column gap-1">
                <span class="text-600">{{ stat.label }}</span>
                <span class="text-2xl font-semibold">{{ formatCurrency(stat.value) }}</span>
              </div>
            </div>
            <div :class="[
              'flex align-items-center justify-content-center border-round-xl px-3 py-2',
              stat.trend === 'up' ? 'bg-green-50' : 'bg-red-50'
            ]">
              <div class="flex align-items-center gap-1">
                <i :class="[
                  stat.trend === 'up' ? 'pi pi-arrow-up text-green-600' : 'pi pi-arrow-down text-red-600',
                  'text-sm'
                ]"></i>
                <span :class="[
                  'text-sm font-medium',
                  stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                ]">{{ stat.change }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
      <div class="lg:col-span-2">
        <div class="bg-white p-6 rounded-xl border border-gray-100 shadow-md">
          <Line :data="chartData" :options="chartOptions" class="h-[280px]" />
        </div>
      </div>
      <div class="lg:col-span-1">
        <div class="bg-white p-6 rounded-xl border border-gray-100 shadow-md">
          <ExpenseDistributionChart :start-date="dates[0]" :end-date="dates[1]" />
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
      <div class="lg:col-span-2">
        <div class="bg-white p-6 rounded-xl border border-gray-100 shadow-md">
          <MonthlyComparisonChart :start-date="dates[0]" :end-date="dates[1]" />
        </div>
      </div>
      <div>
        <div class="bg-white p-6 rounded-xl border border-gray-100 shadow-md">
          <AccountStatusChart :start-date="dates[0]" :end-date="dates[1]" />
        </div>
      </div>
    </div>

    <!-- Recent Activities -->
    <div class="bg-white p-6 rounded-xl border border-gray-100 shadow-md">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-lg font-semibold text-gray-900">Contas Recentes</h2>
        <router-link 
          to="/accounts" 
          class="text-primary-dark hover:text-primary-dark/80 flex items-center gap-2"
        >
          Ver todas
          <i class="pi pi-list text-sm"></i>
        </router-link>
      </div>
      <div v-if="loading || accountsLoading" class="flex justify-center items-center h-48">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light"></div>
      </div>
      <div v-else-if="!recentAccounts.length" class="flex justify-center items-center h-48">
        <p class="text-gray-500">Nenhuma conta recente encontrada</p>
      </div>
      <div v-else class="space-y-4">
        <div v-for="account in recentAccounts" :key="account.id" class="flex items-start space-x-4">
          <div class="w-8 h-8 rounded-full bg-primary-dark/10 flex items-center justify-center flex-shrink-0">
            <i :class="[
              'text-primary-dark text-sm pi',
              account.type === 'payable' ? 'pi-arrow-up' : 'pi-arrow-down'
            ]"></i>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between">
              <p class="text-sm font-medium text-gray-900">{{ getEntityName(account) }}</p>
              <span :class="[
                'text-sm font-medium',
                account.type === 'payable' ? 'text-red-600' : 'text-green-600'
              ]">
                {{ account.type === 'payable' ? '-' : '+' }}{{ formatCurrency(account.value) }}
              </span>
            </div>
            <div class="flex items-center justify-between mt-1">
              <span class="text-xs text-gray-400">Vence em {{ formatDate(account.dueDate) }}</span>
              <span :class="[
                'text-xs px-2 py-1 rounded-full',
                account.type === 'payable' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              ]">
                {{ account.type === 'payable' ? 'A Pagar' : 'A Receber' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Line } from 'vue-chartjs'
import Calendar from 'primevue/calendar'
import ExpenseDistributionChart from '@/components/dashboard/ExpenseDistributionChart.vue'
import MonthlyComparisonChart from '@/components/dashboard/MonthlyComparisonChart.vue'
import AccountStatusChart from '@/components/dashboard/AccountStatusChart.vue'
import { useAuthStore } from '../stores/auth'
import { useRouter } from 'vue-router'
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js'
import { useAccountStore } from '../stores/account'
import { useCustomerStore } from '../stores/customer'
import { useSuppliersStore } from '../stores/suppliers'
import { useCurrentOrganizationStore } from '../stores/currentOrganization'
import { storeToRefs } from 'pinia'
import { chartConfig } from '../config/chart'

// Registrar plugins do Chart.js
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler
)

// Aplicar configuração global
ChartJS.defaults = {
  ...ChartJS.defaults,
  ...chartConfig
}

const accountStore = useAccountStore()
const customerStore = useCustomerStore()
const supplierStore = useSuppliersStore()
const currentOrganizationStore = useCurrentOrganizationStore()
const authStore = useAuthStore()
const router = useRouter()

const { accounts, loading: accountsLoading } = storeToRefs(accountStore)
const { customers } = storeToRefs(customerStore)
const { suppliers } = storeToRefs(supplierStore)
const { currentOrganization } = storeToRefs(currentOrganizationStore)

const loading = ref(true)
const customersLoaded = ref(false)
const suppliersLoaded = ref(false)
const chartInstance = ref(null)

// Data range
const dates = ref([
  new Date(new Date().getFullYear(), new Date().getMonth(), 1), // Primeiro dia do mês atual
  new Date() // Hoje
])

const currentMonthRange = computed(() => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return { firstDay, lastDay }
})

// Observar mudanças na autenticação
watch(() => authStore.isAuthenticated, (isAuthenticated) => {
  if (!isAuthenticated) {
    console.log('[Dashboard] Usuário deslogado, limpando dados...')
    if (accounts.value) accounts.value = []
    if (customers.value) customers.value = []
    if (suppliers.value) suppliers.value = []
  }
})

onMounted(async () => {
  if (!authStore.isAuthenticated) {
    console.log('[Dashboard] Usuário não autenticado, redirecionando...')
    router.push('/login')
    return
  }

  try {
    if (!currentOrganization.value?.id) {
      console.error('[Dashboard] ID da organização não encontrado')
      return
    }

    loading.value = true
    await Promise.all([
      accountStore.fetchAccounts(currentOrganization.value.id),
      customerStore.fetchCustomers(currentOrganization.value.id),
      supplierStore.getSuppliers(currentOrganization.value.id)
    ])

    customersLoaded.value = true
    suppliersLoaded.value = true
  } catch (error) {
    console.error('[Dashboard] Erro ao carregar dados:', error)
  } finally {
    loading.value = false
  }
})

const recentAccounts = computed(() => {
  if (!accounts.value) return []
  
  // Filtra as contas dentro do período selecionado
  const filteredAccounts = accounts.value.filter(acc => {
    const accDate = new Date(acc.dueDate)
    return accDate >= dates.value[0] && accDate <= dates.value[1]
  })
  
  return filteredAccounts
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5)
})

const formatCurrency = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(numericValue)
}

const chartData = computed(() => {
  if (!accounts.value?.length) {
    return {
      labels: [],
      datasets: [{
        label: 'Saldo',
        data: [],
        borderColor: '#003333',
        backgroundColor: 'rgba(0, 102, 102, 0.1)',
        fill: true
      }]
    }
  }

  const startDate = dates.value[0]
  const endDate = dates.value[1]
  
  // Filtra as contas dentro do período selecionado
  const filteredAccounts = accounts.value.filter(acc => {
    const accDate = new Date(acc.dueDate)
    return accDate >= startDate && accDate <= endDate
  })

  // Agrupa por dia
  const dailyBalances = new Map()
  filteredAccounts.forEach(acc => {
    const date = acc.dueDate.split('T')[0]
    const currentBalance = dailyBalances.get(date) || 0
    dailyBalances.set(date, currentBalance + (acc.type === 'INCOME' ? acc.value : -acc.value))
  })

  // Converte para arrays ordenados
  const sortedDates = Array.from(dailyBalances.keys()).sort()
  const balances = sortedDates.map(date => dailyBalances.get(date))

  return {
    labels: sortedDates.map(date => {
      const [year, month, day] = date.split('-')
      return `${day}/${month}`
    }),
    datasets: [{
      label: 'Saldo',
      data: balances,
      borderColor: '#003333',
      backgroundColor: 'rgba(0, 102, 102, 0.1)',
      fill: true,
      tension: 0.4,
      borderWidth: 2,
      pointBackgroundColor: '#003333',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  }
})

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 500
  },
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        padding: 20,
        boxWidth: 6,
        boxHeight: 6
      }
    },
    title: {
      display: true,
      text: 'Variação de Saldo',
      font: {
        size: 14,
        weight: 'bold'
      },
      padding: {
        bottom: 15
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxRotation: 0,
        font: {
          size: 11
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        borderDash: [2, 2]
      },
      ticks: {
        font: {
          size: 11
        },
        callback: (value: number) => {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            notation: 'compact'
          }).format(value)
        }
      }
    }
  }
}))

// Função para determinar o status real da conta
function determineAccountStatus(account: any) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const dueDate = new Date(account.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  
  const scheduledDate = account.scheduledDate ? new Date(account.scheduledDate) : null
  if (scheduledDate) scheduledDate.setHours(0, 0, 0, 0)
  
  // Se a conta está cancelada
  if (account.status === 'CANCELADO') return 'CANCELADO'
  
  // Se tem data agendada
  if (scheduledDate) {
    // Se a data agendada já passou, conta está paga
    if (scheduledDate < today) {
      return 'PAGO'
    }
    // Se a data agendada é hoje ou futura, conta está agendada
    return 'AGENDADO'
  }
  
  // Se não tem data agendada, verifica o vencimento
  if (dueDate < today) {
    return 'ATRASADO'
  }
  
  // Se a data de vencimento é hoje ou futura, está pendente
  return 'PENDENTE'
}

// Função para calcular as métricas financeiras
const calculateFinancialMetrics = computed(() => {
  if (!accounts.value?.length) return { revenue: 0, expenses: 0, profit: 0 }

  const startDate = dates.value[0]
  const endDate = dates.value[1]

  // Filtra as contas do período e que estão pagas
  const filteredAccounts = accounts.value.filter(acc => {
    const accDate = new Date(acc.dueDate)
    return accDate >= startDate && 
           accDate <= endDate && 
           determineAccountStatus(acc) === 'PAGO'
  })

  // Calcula receitas e despesas
  const metrics = filteredAccounts.reduce((acc, curr) => {
    const value = curr.value || 0
    if (curr.type === 'receivable') {
      acc.revenue += value
    } else if (curr.type === 'payable') {
      acc.expenses += value
    }
    return acc
  }, { revenue: 0, expenses: 0 })

  // Calcula o lucro
  metrics.profit = metrics.revenue - metrics.expenses

  return metrics
})

// Função para calcular a variação percentual
const calculatePercentageChange = (current: number, previous: number): string => {
  if (previous === 0) return '0%'
  const change = ((current - previous) / Math.abs(previous)) * 100
  return `${Math.abs(change).toFixed(1)}%`
}

// Calcula as métricas do período anterior para comparação
const calculatePreviousMetrics = computed(() => {
  if (!accounts.value?.length) return { revenue: 0, expenses: 0, profit: 0 }

  const currentStart = dates.value[0]
  const currentEnd = dates.value[1]
  const daysDiff = Math.ceil((currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24))
  
  const previousStart = new Date(currentStart)
  previousStart.setDate(previousStart.getDate() - daysDiff)
  const previousEnd = new Date(currentStart)
  previousEnd.setDate(previousEnd.getDate() - 1)

  // Filtra as contas do período anterior
  const filteredAccounts = accounts.value.filter(acc => {
    const accDate = new Date(acc.dueDate)
    return accDate >= previousStart && 
           accDate <= previousEnd && 
           determineAccountStatus(acc) === 'PAGO'
  })

  // Calcula receitas e despesas do período anterior
  const metrics = filteredAccounts.reduce((acc, curr) => {
    const value = curr.value || 0
    if (curr.type === 'receivable') {
      acc.revenue += value
    } else if (curr.type === 'payable') {
      acc.expenses += value
    }
    return acc
  }, { revenue: 0, expenses: 0 })

  metrics.profit = metrics.revenue - metrics.expenses
  return metrics
})

// Função para calcular o total de investimentos
const calculateInvestments = computed(() => {
  if (!accounts.value?.length) return 0
  
  const startDate = dates.value[0]
  const endDate = dates.value[1]
  
  return accounts.value
    .filter(account => {
      const dueDate = new Date(account.dueDate)
      return account.type === 'payable' && 
        account.costCenterName?.toLowerCase().includes('investimento') &&
        dueDate >= startDate && 
        dueDate <= endDate
    })
    .reduce((total, account) => total + account.value, 0)
})

// Função para calcular o total de pró-labore
const calculateProLabore = computed(() => {
  if (!accounts.value?.length) return 0

  const startDate = dates.value[0]
  const endDate = dates.value[1]

  return accounts.value
    .filter(account => {
      const dueDate = new Date(account.dueDate)
      return account.type === 'payable' && 
        account.costTypeName?.toLowerCase().trim() === 'pró-labore' &&
        dueDate >= startDate && 
        dueDate <= endDate
    })
    .reduce((total, account) => total + account.value, 0)
})

// Função para calcular as despesas (excluindo investimentos e pró-labore)
const calculateExpenses = computed(() => {
  if (!accounts.value?.length) return 0
  
  const startDate = dates.value[0]
  const endDate = dates.value[1]
  
  return accounts.value
    .filter(account => {
      const dueDate = new Date(account.dueDate)
      return account.type === 'payable' && 
        !account.costCenterName?.toLowerCase().includes('investimento') &&
        !account.costTypeName?.toLowerCase().trim().includes('pró-labore') &&
        dueDate >= startDate && 
        dueDate <= endDate
    })
    .reduce((total, account) => total + account.value, 0)
})

// Função para calcular o total de receitas
const calculateRevenue = computed(() => {
  if (!accounts.value?.length) return 0

  const startDate = dates.value[0]
  const endDate = dates.value[1]

  return accounts.value
    .filter(account => {
      const dueDate = new Date(account.dueDate)
      return account.type === 'receivable' &&
        dueDate >= startDate && 
        dueDate <= endDate
    })
    .reduce((total, account) => total + account.value, 0)
})

// Função para calcular a margem de lucro
const calculateProfitMargin = computed(() => {
  const revenue = calculateRevenue.value
  const expenses = calculateExpenses.value
  return revenue - expenses
})

// Função para calcular o lucro líquido
const calculateNetProfit = computed(() => {
  const profitMargin = calculateProfitMargin.value
  const investments = calculateInvestments.value
  const proLabore = calculateProLabore.value
  return profitMargin - investments - proLabore
})

// Stats cards data
const stats = computed(() => {
  const currentMetrics = calculateFinancialMetrics.value
  const previousMetrics = calculatePreviousMetrics.value
  const currentProLabore = calculateProLabore.value
  const currentInvestments = calculateInvestments.value
  const currentExpenses = calculateExpenses.value
  const currentProfitMargin = calculateProfitMargin.value
  const currentNetProfit = calculateNetProfit.value

  // Calcula o lucro líquido do período anterior
  const previousNetProfit = (previousMetrics.revenue - previousMetrics.expenses) - 
    (previousMetrics.investments || 0) - (previousMetrics.proLabore || 0)

  return [
    {
      label: 'Receita Total',
      value: currentMetrics.revenue,
      icon: 'pi pi-dollar',
      trend: currentMetrics.revenue >= previousMetrics.revenue ? 'up' : 'down',
      change: calculatePercentageChange(currentMetrics.revenue, previousMetrics.revenue)
    },
    {
      label: 'Despesas',
      value: currentExpenses,
      icon: 'pi pi-wallet',
      trend: currentExpenses <= previousMetrics.expenses ? 'up' : 'down',
      change: calculatePercentageChange(currentExpenses, previousMetrics.expenses)
    },
    {
      label: 'Margem de Lucro',
      value: currentProfitMargin,
      icon: 'pi pi-chart-line',
      trend: currentProfitMargin >= (previousMetrics.revenue - previousMetrics.expenses) ? 'up' : 'down',
      change: calculatePercentageChange(currentProfitMargin, (previousMetrics.revenue - previousMetrics.expenses))
    },
    {
      label: 'Investimento',
      value: currentInvestments,
      icon: 'pi pi-chart-bar',
      trend: 'up',
      change: '0%'
    },
    {
      label: 'Pró-Labore',
      value: currentProLabore,
      icon: 'pi pi-user',
      trend: 'up',
      change: '0%'
    },
    {
      label: 'Lucro Líquido',
      value: currentNetProfit,
      icon: 'pi pi-percentage',
      trend: currentNetProfit >= previousNetProfit ? 'up' : 'down',
      change: calculatePercentageChange(currentNetProfit, previousNetProfit)
    }
  ]
})

// Get entity name from account
const getEntityName = (account: any): string => {
  if (account.type === 'payable') {
    const supplier = suppliers.value.find(s => s.id === account.supplierId)
    return supplier ? supplier.name : 'Carregando...'
  } else {
    const customer = customers.value.find(c => c.id === account.customerId)
    return customer ? customer.name : 'Carregando...'
  }
}

// Format date to dd/mm/yyyy
function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

// Função para resetar as datas para o mês atual
const resetToCurrentMonth = () => {
  dates.value = [
    new Date(new Date().getFullYear(), new Date().getMonth(), 1), // Primeiro dia do mês atual
    new Date() // Hoje
  ]
}
</script>

<style scoped>
:deep(.p-calendar) {
  width: 100%;
}

:deep(.p-calendar .p-inputtext) {
  font-family: var(--font-family);
  font-size: var(--bs-body-font-size);
  color: var(--surface-600);
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  padding: 0.5rem;
  border-radius: var(--border-radius);
  transition: all 0.2s;
  width: 100%;
  height: 35px;
}

:deep(.p-calendar .p-inputtext:hover) {
  border-color: var(--primary-400);
}

:deep(.p-calendar .p-inputtext:focus) {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: var(--focus-ring);
}

:deep(.p-calendar .p-button) {
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  color: var(--surface-600);
  height: 35px;
}

:deep(.p-calendar .p-button:hover) {
  background: var(--surface-100);
  border-color: var(--primary-400);
  color: var(--surface-700);
}

:deep(.p-calendar .p-button:focus) {
  box-shadow: var(--focus-ring);
}

:deep(.p-datepicker) {
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  box-shadow: var(--bs-box-shadow);
}

:deep(.p-datepicker .p-datepicker-header) {
  background: var(--surface-0);
  color: var(--surface-700);
  border-bottom: 1px solid var(--surface-border);
}

:deep(.p-datepicker .p-datepicker-calendar td) {
  padding: 0.2rem;
}

:deep(.p-datepicker .p-datepicker-calendar td > span) {
  width: 2rem;
  height: 2rem;
  border-radius: var(--border-radius);
  transition: all 0.2s;
}

:deep(.p-datepicker .p-datepicker-calendar td > span.p-highlight) {
  background: var(--primary-50);
  color: var(--primary-600);
}

:deep(.p-datepicker .p-datepicker-calendar td > span:not(.p-disabled):hover) {
  background: var(--surface-hover);
}

/* Estilos dos cards */
.surface-card {
  background: var(--surface-0);
  transition: all 0.2s ease;
  border: 1px solid var(--surface-200);
  box-shadow: var(--bs-box-shadow);
}

.surface-card:hover {
  border-color: var(--surface-400);
}

.border-round-xl {
  border-radius: 1rem;
}

.w-3rem {
  width: 3rem;
}

.h-3rem {
  height: 3rem;
}

.bg-green-50 {
  background-color: var(--green-50);
}

.bg-red-50 {
  background-color: var(--red-50);
}

.bg-blue-50 {
  background-color: var(--blue-50);
}

.bg-orange-50 {
  background-color: var(--orange-50);
}

.bg-purple-50 {
  background-color: var(--purple-50);
}

.bg-cyan-50 {
  background-color: var(--cyan-50);
}

.text-green-500 {
  color: var(--green-500);
}

.text-red-500 {
  color: var(--red-500);
}

.text-blue-500 {
  color: var(--blue-500);
}

.text-orange-500 {
  color: var(--orange-500);
}

.text-purple-500 {
  color: var(--purple-500);
}

.text-cyan-500 {
  color: var(--cyan-500);
}

.text-green-600 {
  color: var(--green-600);
}

.text-red-600 {
  color: var(--red-600);
}

.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
</style>